import React, { useRef } from "react";
import { Modal, Box, Typography, Grid, List, ListItem } from "@mui/material";
import { MButton } from "@app/components";
import { LabelKeys, LabelTypesOptions } from "@app/types/samples.types";
import {
  toExponentialUppercase,
  toExponentialUppercaseWhitZero,
} from "@app/utils/functions";
import { filterAndSortData } from "@app/pages/samples/utils/helpers";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  px: 4,
  py: 2,
  overflowY: "auto",
};

interface DataModalProps {
  open: boolean;
  handleClose: () => void;
  data: any;
  typeAnalysis: any;
  efficiencieTotal?: number;
  efficiencieMedia?: number;
}

export const DataModalPuAmCMGenie = ({
  open,
  handleClose,
  data,
  typeAnalysis,
  efficiencieTotal,
  efficiencieMedia,
}: DataModalProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const typeAnalysisName = typeAnalysis?.descripcion || "";

  const formattedData = {
    ...data,
    calculation_id: data?.calculation?.id,
  };

  if (!data || Object.keys(data).length === 0) {
    formattedData.efficiency = null;
    formattedData.inc_rel_squared_performance = null;
    formattedData.efficiency_sr_89 = null;
  }

  delete formattedData.calculation;
  const isCurrier = false;

  const { arrrayKeys, extendedLabelString } = filterAndSortData(
    formattedData,
    isCurrier,
    typeAnalysis?.codigo
  );

  const updatedArrayKeys = arrrayKeys.map(([key, value]) => {
    if (key === "efficiency") {
      return [key, efficiencieTotal ?? null];
    }
    if (key === "efficiency_sr_89") {
      return [key, efficiencieMedia ?? null];
    }
    if (key === "inc_rel_squared_performance") {
      let detectorPerformance = 0;
      if (efficiencieMedia && efficiencieTotal)
        detectorPerformance = (efficiencieTotal / efficiencieMedia) * 100;
      return [key, detectorPerformance ?? null];
    }
    return [key, value];
  });

  const formatValue = (value: any, type: string, decimals: number) => {
    if (value === null || value === undefined) {
      return ""; // Devolver cadena vacía en lugar de undefined
    }
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return value;
    }
    if (type === LabelTypesOptions.NUMERICO) {
      return numericValue.toFixed(decimals);
    } else if (type === LabelTypesOptions.CIENTIFICO) {
      return toExponentialUppercase(numericValue, decimals);
    } else if (type === LabelTypesOptions.CIENTIFICOCONCERO) {
      return toExponentialUppercaseWhitZero(numericValue, 2);
    }
    return value;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={style} ref={modalContentRef}>
        <Typography
          id="modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 0, borderBottom: 3, borderColor: "divider" }}
        >
          Datos de salida {typeAnalysisName && ` - ${typeAnalysisName}`}
        </Typography>
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {updatedArrayKeys.map(([key, value], index) => {
            const { label, decimals, type } =
              extendedLabelString[key as LabelKeys];
            return (
              <ListItem key={index} disableGutters sx={{ p: 0 }}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0, borderBottom: 1, borderColor: "divider" }}
                >
                  <Grid item xs={8} sm={9} md={10}>
                    <Typography
                      noWrap
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={3} md={2}>
                    <Typography sx={{ textAlign: "right" }}>
                      {value === 0
                        ? "0"
                        : value
                        ? formatValue(value, type, decimals)
                            .toString()
                            .toUpperCase()
                        : ""}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
            );
          })}
        </List>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            gap: 2,
          }}
        >
          <MButton
            variant="contained"
            color="primary"
            text="Cerrar"
            onClick={handleClose}
          />
        </Box>
      </Box>
    </Modal>
  );
};